/**
 * Plans v2 card 3.4: what the SERVER says about this account's cloud, read in
 * ONE place. Shape of the backend (Apiboo/apiboo_backend_phoenix#217,
 * `Plans.CloudAccess`):
 *
 *   GET /api/v1/auth/usage →
 *     { …, "cloud": {
 *         "access": bool,              // the plan includes personal cloud sync
 *         "read_only": bool,           // !access && has_data
 *         "has_data": bool,            // personal records, vault or personal workspaces
 *         "removal_flagged_at": ISO | null,  // a cloud → no-cloud change; from
 *                                            // 16.10.2026 also old Free cloud data
 *         "removal_scheduled_at": ISO | null,  // NEW (backend branch
 *                                            // 16_10_2026_retention_ai_tokens):
 *                                            // the deletion date, flag + 90 days;
 *                                            // may be absent
 *         "removal_reason": "downgrade" | "legacy_no_cloud",  // NEW, same PR
 *         "required_plan": "pro" } }
 *   GET /api/v1/auth/user → { …, "cloud_removal_flagged_at": ISO | null,
 *                                "cloud_removal_scheduled_at": ISO | null,
 *                                "cloud_removal_reason": … }
 *
 *   A refused personal cloud write → HTTP 402
 *     { "error": "…", "upgrade_required": true, "required_plan": "pro", "read_only": true }
 *   (the no-cloud refusal is the same without `read_only`).
 *
 * An older backend sends no `cloud` block: then nothing here is known, and
 * nothing is ever locked on it — a paying user must not lose their cloud
 * because the server is older than the app. Pure: no store, no invoke (it
 * sits in the MCP import graph through records/itemLocation).
 */

/** The capability that lets an account keep items in the cloud. */
export const CLOUD_SYNC = 'cloud_sync';

/** Plans that have `cloud_sync`, for when no server answer is known yet. */
const CLOUD_PLANS = Object.freeze(['pro', 'team', 'enterprise']);

const isIso = (v) => typeof v === 'string' && v.trim() !== '';
const boolOrNull = (v) => (typeof v === 'boolean' ? v : null);

/** The usage answer's `cloud` block, or null (older backend, no answer). */
function cloudBlock(usage) {
  const c = usage?.cloud;
  return c && typeof c === 'object' && typeof c.access === 'boolean' ? c : null;
}

/**
 * Why the server flagged the cloud copy (owner, 16.10.2026):
 *   'downgrade'       — a plan with cloud sync lapsed or was changed: no
 *                       automatic copy, "Download to this device" + export;
 *   'legacy_no_cloud' — an old Free account's cloud data: copied automatically.
 */
export const REMOVAL_REASONS = Object.freeze(['downgrade', 'legacy_no_cloud']);

/** An ISO string that is also a real date, else null. */
function validDate(v) {
  if (!isIso(v)) return null;
  return Number.isNaN(Date.parse(v)) ? null : v;
}

/**
 * `{ flaggedAt, scheduledAt, readOnly, hasData }` from a /auth/usage answer
 * and, for the two dates, the /auth/user object:
 *   flaggedAt:   ISO time the server flagged the cloud copy for removal (a
 *                downgrade, or an old Free account's cloud data), else null;
 *   scheduledAt: ISO time the cloud copy WILL BE DELETED (90-day retention,
 *                owner 16.10.2026; `removal_scheduled_at`), else null. A
 *                backend that predates it sends nothing: then no date is
 *                shown, never a guessed one. On /auth/user it is
 *                `cloud_removal_scheduled_at`;
 *   reason:      why it was flagged — `REMOVAL_REASONS` (`removal_reason`,
 *                /auth/user `cloud_removal_reason`); null when the server
 *                said nothing or something unknown;
 *   readOnly / hasData: the server's booleans, null when it said nothing.
 */
export function readCloudRemoval(usage, user) {
  const c = cloudBlock(usage);
  const flag = c && isIso(c.removal_flagged_at) ? c.removal_flagged_at : user?.cloud_removal_flagged_at;
  const scheduled = (c && validDate(c.removal_scheduled_at)) || validDate(user?.cloud_removal_scheduled_at);
  const reason = c && REMOVAL_REASONS.includes(c.removal_reason) ? c.removal_reason : user?.cloud_removal_reason;
  return {
    flaggedAt: isIso(flag) ? flag : null,
    scheduledAt: scheduled || null,
    reason: REMOVAL_REASONS.includes(reason) ? reason : null,
    readOnly: c ? boolOrNull(c.read_only) : null,
    hasData: c ? boolOrNull(c.has_data) : null,
  };
}

/**
 * The deletion date for people, in the app's language: "16 January 2027" /
 * "16. Januar 2027". Null for no date or a date that does not parse.
 */
export function formatCloudRemovalDate(iso, language = 'en') {
  const at = validDate(iso);
  if (!at) return null;
  const locale = language === 'de' ? 'de-DE' : 'en-GB';
  try {
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(at));
  } catch {
    return at.slice(0, 10);
  }
}

/**
 * The capability list a /auth/usage answer implies: its own `capabilities`
 * when it sends them, else `cloud_sync` from `cloud.access`. Null when it
 * says nothing about the cloud (an older backend): unknown, never "no".
 */
export function capabilitiesFromUsage(usage) {
  if (Array.isArray(usage?.capabilities)) return usage.capabilities.filter((c) => typeof c === 'string');
  const c = cloudBlock(usage);
  if (!c) return null;
  return c.access ? [CLOUD_SYNC] : [];
}

/**
 * May this account keep items in the cloud?
 *   `{ allowed, known }` — `known` only when the server said so (the signed
 *   claim, or /auth/usage); otherwise `allowed` is the plan name's best guess.
 * `entitlements` is the entitlements store's state.
 */
export function cloudSyncFrom(entitlements) {
  const e = entitlements || {};
  if (e.source === 'synthetic') return { allowed: true, known: true };
  if (e.capabilitiesKnown && Array.isArray(e.capabilities)) {
    return { allowed: e.capabilities.includes(CLOUD_SYNC), known: true };
  }
  return { allowed: CLOUD_PLANS.includes(String(e.plan || '').toLowerCase()), known: false };
}

/**
 * A 402 body (object, JSON string, or the engine's "402 Payment Required: {…}"
 * message) as `{ upgradeRequired, readOnly, error }`, or null when it is not
 * the backend's cloud plan refusal.
 */
export function cloudPlanRefusal(body) {
  let b = body;
  // The per-op verdict of a mixed batch (deletes applied, the rest refused):
  // `{"status": "rejected", "error": "read_only", …}` — the engine forwards
  // only the `error` code.
  if (b === 'read_only') return { upgradeRequired: true, readOnly: true, error: '' };
  if (typeof b === 'string') {
    const at = b.indexOf('{');
    if (at < 0) return null;
    try {
      b = JSON.parse(b.slice(at));
    } catch {
      return null;
    }
  }
  if (!b || typeof b !== 'object' || b.upgrade_required !== true) return null;
  return { upgradeRequired: true, readOnly: b.read_only === true, error: typeof b.error === 'string' ? b.error : '' };
}
